'use client'

import { useRef, useEffect, useState } from 'react'
import gsap from 'gsap'
import { STATS } from '../data/stats'

interface PreloaderProps {
  onComplete: () => void
  /** True once the HexField shaders/data have finished loading (during the loader). */
  ready: boolean
}

/* Boot self-test narration — the count is the sole conductor; everything derives from it.
   The two middle lines POST the REAL career figures (single-sourced from data/stats),
   so the loader shows honest numbers, not filler — the same ones the Signal counters land on. */
const LABELS = ['Boot', 'Post', 'Warmup', 'Warmup']
const LOGS = [
  'cold start · core/void ......... OK',
  `link network · ${STATS.network}+ nodes ... OK`,
  `load builds · ${STATS.builds}+ shipped ... OK`,
  'resolve SUBJECT.01 ............',
]
const SEG_COUNT = 7
const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'
const LIT_GRAD = 'linear-gradient(90deg, var(--color-ember), var(--color-fire), var(--color-blaze))'

/**
 * Preloader — IGNITION GAUGE // COLD START → IDENTITY HANDSHAKE
 * A cinematic power-on self-test: the gauge charges, the boot log POSTs, and the final
 * "RESOLVE SUBJECT.01" check stays pending until `ready` fires — then snaps to LOCK and
 * collapses the console into the hero's reticle, where the particle face assembles.
 *
 * Perf-critical mechanics PRESERVED verbatim: brisk cosmetic fill (never gates LCP) →
 * HOLD until `ready` (rAF gate) → MAX_WAIT fallback → exit → onComplete(). One textContent
 * write + one dot transform per frame; segments/log/label flip only on discrete state change.
 */
export default function Preloader({ onComplete, ready }: PreloaderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const glowDotRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const orangeRef = useRef<SVGSVGElement>(null)
  const percentRef = useRef<HTMLSpanElement>(null)
  const statusRef = useRef<HTMLSpanElement>(null)
  const dotRef = useRef<HTMLSpanElement>(null)
  const logRef = useRef<HTMLSpanElement>(null)
  const [isVisible, setIsVisible] = useState(true)

  // Mirror `ready` into a ref so the rAF wait loop always sees the latest value.
  const readyRef = useRef(ready)
  useEffect(() => { readyRef.current = ready }, [ready])

  useEffect(() => {
    if (!containerRef.current) return

    document.body.style.overflow = 'hidden'

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const counter = { val: 0 }
    let waitRaf = 0
    let finished = false
    let lastBand = -1

    // Single source of truth: % + log line + status label derive from `v`; the gauge fills
    // smoothly via its own scaleX tween (continuous, not stepped).
    const applyState = (v: number) => {
      if (percentRef.current) percentRef.current.textContent = `${v}%`
      // The emblem is a small orange that RIPENS as the field charges: it starts a
      // dim, desaturated sketch and fills to full fire + glow as v → 100 — the
      // through-object being born on frame one (the hexagon's successor).
      if (orangeRef.current) {
        const t = v / 100
        orangeRef.current.style.opacity = (0.3 + 0.7 * t).toFixed(3)
        orangeRef.current.style.filter = `saturate(${(0.35 + 0.65 * t).toFixed(2)}) drop-shadow(0 0 ${(1 + 6 * t).toFixed(1)}px rgba(255,107,0,${(0.15 + 0.5 * t).toFixed(2)}))`
        orangeRef.current.style.transform = `scale(${(0.88 + 0.12 * t).toFixed(3)})`
      }
      const band = v < 25 ? 0 : v < 50 ? 1 : v < 80 ? 2 : 3
      if (band !== lastBand) {
        lastBand = band
        if (logRef.current) logRef.current.textContent = LOGS[band]
        if (statusRef.current) statusRef.current.textContent = LABELS[band]
      }
    }

    // Force the resolved end-state so an exit triggered mid-fill never shows a half-built console.
    const setLockState = () => {
      applyState(100)
      if (fillRef.current) gsap.set(fillRef.current, { scaleX: 1 })
      if (glowDotRef.current) gsap.set(glowDotRef.current, { left: '100%' })
      if (statusRef.current) {
        statusRef.current.textContent = 'Lock'
        statusRef.current.style.color = 'var(--color-fire)'
        statusRef.current.style.animation = 'blink 1s step-end 2'
      }
      if (dotRef.current) {
        dotRef.current.style.background = 'var(--color-fire)'
        dotRef.current.style.boxShadow = '0 0 9px var(--color-fire)'
        dotRef.current.style.animation = 'dot-pulse 2.4s ease-in-out infinite'
      }
      if (logRef.current) logRef.current.textContent = 'resolve SUBJECT.01 ... LOCK'
    }

    const cleanup = () => {
      document.body.style.overflow = ''
      setIsVisible(false)
      onComplete()
    }

    // Exit: complete the honest check, then collapse the console into the hero's reticle.
    const finish = () => {
      if (finished) return
      finished = true
      setLockState()

      if (reduce) {
        gsap.to(containerRef.current, { opacity: 0, duration: 0.3, ease: 'power2.inOut', onComplete: cleanup })
        return
      }

      const tl2 = gsap.timeline({ onComplete: cleanup })
      // Beat 1 — the lit console (with LOCK) lingers a beat, then strips away
      tl2.to('.preloader-brand, .preloader-system', { opacity: 0, y: -8, duration: 0.28, ease: 'power2.in' }, '+=0.15')

      // Beat 2 — a hexagonal APERTURE is punched through the shroud at the hero
      // reticle's centre (50%, 47%) and grows until the hero fills the screen.
      // Donut clip-path (outer rect + reverse-wound inner hex) keeps the hex
      // silhouette razor-crisp at every size — no scaled-sprite blur.
      const shroud = containerRef.current?.querySelector<HTMLElement>('.preloader-shroud')
      const rim = containerRef.current?.querySelector<HTMLElement>('.preloader-hex-rim')
      const vw = window.innerWidth
      const vh = window.innerHeight
      const cx = vw / 2
      const cy = vh * 0.47
      const rMax = Math.hypot(Math.max(cx, vw - cx), Math.max(cy, vh - cy)) * 1.35
      const hole = { r: 0 }
      const setAperture = () => {
        const r = hole.r
        const pts: string[] = []
        for (let k = 0; k <= 6; k++) {
          const a = ((-90 - k * 60) * Math.PI) / 180
          pts.push(`${(((cx + r * Math.cos(a)) / vw) * 100).toFixed(2)}% ${(((cy + r * Math.sin(a)) / vh) * 100).toFixed(2)}%`)
        }
        if (shroud) shroud.style.clipPath = `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${pts.join(', ')}, 0% 0%)`
        if (rim) {
          rim.style.width = `${r * 2}px`
          rim.style.height = `${r * 2}px`
          rim.style.opacity = r > 2 ? '1' : '0'
        }
      }
      // …opens to the reticle's size and holds a breath (the handoff frame)…
      tl2.to(hole, { r: Math.min(vw, vh) * 0.26, duration: 0.45, ease: 'power2.out', onUpdate: setAperture }, '-=0.1')
      // …then swallows the screen — the hero is revealed THROUGH the hexagon.
      tl2.to(hole, { r: rMax, duration: 0.8, ease: 'power3.in', onUpdate: setAperture }, '+=0.12')
      if (rim) tl2.to(rim, { opacity: 0, duration: 0.22, ease: 'power1.out' }, '-=0.12')
    }

    // After the bar reaches 100%, hold until the shaders are ready.
    // Fallback: never block longer than MAX_WAIT so a slow/failed WebGL warm-up can't trap
    // the user behind the loader.
    const MAX_WAIT = 4000
    let waitStart = 0
    const waitForReady = () => {
      if (!waitStart) waitStart = performance.now()
      if (readyRef.current || performance.now() - waitStart > MAX_WAIT) {
        finish()
        return
      }
      waitRaf = requestAnimationFrame(waitForReady)
    }

    // Reduced motion: snap to the charged end-state, still gate on ready → finish → onComplete.
    if (reduce) {
      gsap.set(['.preloader-brand', '.preloader-system'], { opacity: 1, y: 0 })
      if (fillRef.current) gsap.set(fillRef.current, { scaleX: 1 })
      if (glowDotRef.current) gsap.set(glowDotRef.current, { left: '100%' })
      applyState(100)
      waitForReady()
      return () => { cancelAnimationFrame(waitRaf) }
    }

    const tl = gsap.timeline({ onComplete: waitForReady })

    // Phase 1: brand fade in
    tl.fromTo('.preloader-brand', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' })
    // Phase 2: system area fade in
    tl.fromTo('.preloader-system', { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' }, '-=0.2')
    // Phase 3: the fill — one tween conducts %, segments, log line, and status label
    tl.to(counter, {
      val: 100,
      duration: 1.4,
      ease: 'power2.inOut',
      onUpdate: () => applyState(Math.round(counter.val)),
    }, '-=0.1')
    // Smooth continuous fill (one scaleX tween — GSAP interpolates every frame, no stepping)
    if (fillRef.current) {
      tl.to(fillRef.current, { scaleX: 1, duration: 1.4, ease: 'power2.inOut' }, '<')
    }
    if (glowDotRef.current) {
      tl.to(glowDotRef.current, { left: '100%', duration: 1.4, ease: 'power2.inOut' }, '<')
    }
    // Phase 4: brief settle at 100% before the ready-gate
    tl.to({}, { duration: 0.2 })

    return () => {
      tl.kill()
      cancelAnimationFrame(waitRaf)
    }
  }, [onComplete])

  if (!isVisible) return null

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{ zIndex: 10000, willChange: 'opacity', transform: 'translateZ(0)' }}
    >
      <span className="sr-only">Loading</span>

      {/* The SHROUD — carries the void backdrop; on exit a hexagonal APERTURE is
          punched through it (clip-path donut) and grows until the hero fills it. */}
      <div className="preloader-shroud absolute inset-0" style={{ backgroundColor: '#050505', willChange: 'clip-path' }}>
        {/* Ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 65% at 50% 45%, rgba(255,107,0,0.07) 0%, rgba(255,80,0,0.03) 40%, transparent 70%)',
          }}
        />

        {/* Faint ghost hexagon watermark (matches the site's ghost-watermark grammar) */}
        <svg
          aria-hidden="true"
          viewBox="0 0 100 100"
          className="absolute pointer-events-none"
          style={{ width: 'min(60vh, 40rem)', opacity: 0.04, left: '50%', top: '45%', transform: 'translate(-50%,-50%)' }}
        >
          <polygon points="50,3 92,27 92,73 50,97 8,73 8,27" fill="none" stroke="var(--color-fire)" strokeWidth="0.5" />
        </svg>
      </div>

      {/* Content — responsive centered layout */}
      <div className="relative flex flex-col items-center w-full" style={{ padding: '0 clamp(1.5rem, 6vw, 4rem)' }}>
        {/* Brand: abd white + orange orange */}
        <div
          className="preloader-brand font-display font-extrabold select-none opacity-0"
          style={{ fontSize: 'clamp(2.5rem, 8vw, 7rem)', letterSpacing: '-0.04em', lineHeight: 1, willChange: 'opacity, transform' }}
        >
          <span style={{ color: '#FAFAFA' }}>abd</span>
          <span style={{ color: '#FF6B00', textShadow: '0 0 30px rgba(255,107,0,0.15), 0 0 60px rgba(255,107,0,0.06)' }}>orange</span>
        </div>

        {/* System ignition console */}
        <div className="preloader-system opacity-0 w-full" style={{ maxWidth: '28rem', marginTop: 'clamp(2rem, 5vw, 4rem)' }}>
          {/* Status row: hex glyph + dot + label  |  count */}
          <div className="flex items-center justify-between" style={{ marginBottom: 'clamp(0.75rem, 2vw, 1.1rem)' }}>
            <div className="flex items-center gap-2">
              {/* The ripening orange — the loader's charge emblem (see applyState) */}
              <svg
                ref={orangeRef}
                width="16"
                height="16"
                viewBox="0 0 100 100"
                fill="none"
                aria-hidden="true"
                style={{ flexShrink: 0, opacity: 0.3, transformOrigin: 'center center', filter: 'saturate(0.35)' }}
              >
                <circle cx="50" cy="50" r="45" stroke="#FF6B00" strokeWidth="8" />
                <line x1="50" y1="50" x2="50" y2="9" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <line x1="50" y1="50" x2="85.5" y2="29.5" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <line x1="50" y1="50" x2="85.5" y2="70.5" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <line x1="50" y1="50" x2="50" y2="91" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <line x1="50" y1="50" x2="14.5" y2="70.5" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <line x1="50" y1="50" x2="14.5" y2="29.5" stroke="#FF6B00" strokeWidth="4" opacity="0.55" />
                <circle cx="50" cy="50" r="7" fill="#FF6B00" />
              </svg>
              <span
                ref={dotRef}
                aria-hidden="true"
                style={{ width: 8, height: 9, flexShrink: 0, clipPath: HEX_DOT, background: 'var(--color-steel-deep)' }}
              />
              <span
                ref={statusRef}
                className="font-mono uppercase"
                style={{ fontSize: 'clamp(0.6rem, 0.9vw, 0.8rem)', letterSpacing: '0.2em', color: 'var(--color-mist)' }}
              >
                Boot
              </span>
            </div>
            <span
              ref={percentRef}
              className="font-mono"
              style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)', color: 'var(--color-light)', fontVariantNumeric: 'tabular-nums' }}
            >
              0%
            </span>
          </div>

          {/* Charge gauge — a SMOOTH continuous fill under a static segmented hex-tick overlay */}
          <div className="relative w-full" style={{ height: 2, backgroundColor: 'rgba(255,107,0,0.12)' }}>
            {/* smooth fill (scaleX tweened) */}
            <div
              ref={fillRef}
              aria-hidden="true"
              className="absolute top-0 left-0 h-full origin-left"
              style={{
                width: '100%', transform: 'scaleX(0)', background: LIT_GRAD,
                boxShadow: '0 0 8px rgba(255,107,0,0.35), 0 0 18px rgba(255,107,0,0.12)',
              }}
            />
            {/* static segment ticks — dark gaps every 1/N so the smooth fill reads as segmented */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{ background: `repeating-linear-gradient(90deg, transparent 0, transparent calc(100% / ${SEG_COUNT} - 2px), #050505 calc(100% / ${SEG_COUNT} - 2px), #050505 calc(100% / ${SEG_COUNT}))` }}
            />
            {/* hex glow dot rides the leading edge */}
            <div
              ref={glowDotRef}
              aria-hidden="true"
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
              style={{
                left: '0%', width: '10px', height: '11px', backgroundColor: 'var(--color-blaze)', clipPath: HEX_DOT,
                boxShadow: '0 0 8px rgba(255,138,0,0.8), 0 0 20px rgba(255,107,0,0.5)',
              }}
            />
          </div>

          {/* Boot log — single fixed-height line, swapped in place (no reflow) */}
          <div
            className="font-mono"
            aria-hidden="true"
            style={{ marginTop: 'clamp(0.7rem, 1.8vw, 1rem)', fontSize: 'clamp(0.55rem, 0.75vw, 0.7rem)', minHeight: '1.2em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            <span style={{ color: 'var(--color-fire)' }}>&gt;</span>{' '}
            <span ref={logRef} style={{ color: 'var(--color-fog)' }}>cold start · core/void ....... OK</span>
          </div>

          {/* Version metadata */}
          <div className="preloader-version flex justify-between" style={{ marginTop: 'clamp(0.75rem, 2vw, 1.1rem)' }}>
            <span className="font-mono" style={{ fontSize: 'clamp(0.55rem, 0.75vw, 0.7rem)', color: 'var(--color-fog)' }}>v.2.0.4-beta</span>
            <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.55rem, 0.75vw, 0.7rem)', letterSpacing: '0.15em', color: 'var(--color-fog)' }}>EST_{STATS.est}</span>
          </div>
        </div>
      </div>

      {/* Aperture RIM — a crisp fire hexagon edging the growing hole. Size is
          synced to the aperture radius each frame; non-scaling stroke keeps the
          edge razor-sharp at ANY scale (the old scaled sprite blurred out). */}
      <div
        className="preloader-hex-rim absolute pointer-events-none"
        style={{ left: '50%', top: '47%', transform: 'translate(-50%,-50%)', width: 0, height: 0, opacity: 0, willChange: 'width, height' }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 200 200" width="100%" height="100%" style={{ overflow: 'visible' }}>
          <polygon
            points="100,10 177.9,55 177.9,145 100,190 22.1,145 22.1,55"
            fill="none" stroke="#FF6B00" strokeWidth="1.6" vectorEffect="non-scaling-stroke"
            style={{ filter: 'drop-shadow(0 0 12px rgba(255,107,0,0.65)) drop-shadow(0 0 30px rgba(255,107,0,0.3))' }}
          />
        </svg>
      </div>
    </div>
  )
}
